/*
 * kaching-card-bridge.js  — PROTOTYPE
 * ---------------------------------------------------------------------------
 * Renders Kaching Bundles tiers as a native <select> on collection cards,
 * WITHOUT reimplementing any of Kaching's discount logic.
 *
 * How it works (all confirmed live on pouches.eu):
 *   1. Each card carries a hidden mount point + a native <select>, both inside
 *      the card's existing {% form 'product' %} (theme snippet product-item).
 *   2. When a card scrolls into view we inject a real <kaching-bundle> element.
 *      Kaching's loader re-scans the DOM and hydrates it (~1.7s, debounced).
 *   3. Once hydrated we read the tiers (.kaching-bundles__bar) and populate the
 *      <select>. Selecting an option clicks the matching tier's <label>, which
 *      makes Kaching write BOTH `quantity` and `properties[__kaching_bundles]`
 *      into the card form itself (async, ~100-300ms after the click).
 *   4. The card's normal "Add to cart" submits that form; the theme's
 *      <product-form> serialises the WHOLE form via new FormData(form) → POST
 *      /cart/add.js, so the tier + discount ride along natively.
 *
 * Hydration state (data-initialized attribute), confirmed live:
 *   - absent      → still loading
 *   - "true"      → hydrated; read .kaching-bundles__bar tiers
 *   - "skipped"   → product has NO deal (must be treated as no-deal immediately,
 *                   NOT as "still loading" — that was the bug that stalled lower
 *                   rows when we queued activation).
 *
 * Activation: we do NOT use IntersectionObserver. The theme's card "reveal"
 * animation keeps off-screen cards at width:1px/opacity:0, which makes IO
 * unreliable (it fired for the first row only). Instead we activate every card
 * up front — Kaching batches its own hydration efficiently (16 widgets injected
 * together hydrate in ~1.1s) and the dropdowns stay hidden until ready, so there
 * is no visual cost. A MutationObserver picks up cards added later by AJAX
 * pagination / filtering / infinite scroll.
 *
 * Selectors are grouped in KB.sel so a Kaching markup change is a one-line fix.
 * ---------------------------------------------------------------------------
 */
(function () {
  if (window.__kbCardBridge) return; // load-once guard (script may be included twice)
  window.__kbCardBridge = true;

  var KB = {
    sel: {
      card: '[data-kb-card]',
      wrap: '[data-kb-wrap]',
      mount: '[data-kb-mount]',
      select: '[data-kb-select]',
      widget: 'kaching-bundle',
      bar: '.kaching-bundles__bar',
      barTitle: '.kaching-bundles__bar-title',
      barPrice: '.kaching-bundles__bar-price',
      barSubtitle: '.kaching-bundles__bar-subtitle', // e.g. "You save 5%"
      barLabel: 'label',
      cardPrice: '.product-item-meta__card-price-current',
      themeVariantSelect: '.select-q',
      addButton: '.product-form__add-button',
      root: 'product-item'
    },
    SAFETY_TIMEOUT_MS: 20000, // absolute give-up ceiling (never destroys a deal)
    NODEAL_SETTLE_MS: 400,    // after "true" with 0 bars, wait before trusting no-deal
    DEFAULT_PACK: 10          // preferred default tier (pack size). Falls back to
                              // Kaching's "Favorite", then the first tier, if absent.
  };

  /* ----- helpers ------------------------------------------------------- */

  function txt(el) { return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }

  function readTiers(widget) {
    return Array.prototype.map.call(widget.querySelectorAll(KB.sel.bar), function (bar) {
      return {
        id: bar.getAttribute('data-deal-bar-id'),
        title: txt(bar.querySelector(KB.sel.barTitle)),
        price: txt(bar.querySelector(KB.sel.barPrice)),
        savings: txt(bar.querySelector(KB.sel.barSubtitle)), // e.g. "You save 5%"
        selected: bar.classList.contains('kaching-bundles__bar--selected'),
        label: bar.querySelector(KB.sel.barLabel)
      };
    }).filter(function (t) { return t.id && t.label; });
  }

  // Resolve with 'deal' (has tiers), 'nodeal' (initialised but no deal), or
  // 'timeout'. Keep waiting ONLY while data-initialized is still absent.
  function whenHydrated(widget, cb) {
    var done = false, obs, safety, settle;
    function conclude(status, tiers) {
      if (done) return;
      done = true;
      if (obs) obs.disconnect();
      clearTimeout(safety); clearTimeout(settle);
      cb(status, tiers || []);
    }
    function check() {
      if (done) return;
      var state = widget.getAttribute('data-initialized');
      if (!state) return;                                   // still loading
      if (state !== 'true') { conclude('nodeal', []); return; } // e.g. "skipped"
      var tiers = readTiers(widget);
      if (tiers.length) { conclude('deal', tiers); return; }
      // initialised as "true" but no bars yet — settle briefly, then decide
      clearTimeout(settle);
      settle = setTimeout(function () {
        var t = readTiers(widget);
        conclude(t.length ? 'deal' : 'nodeal', t);
      }, KB.NODEAL_SETTLE_MS);
    }
    obs = new MutationObserver(check);
    obs.observe(widget, { childList: true, subtree: true, attributes: true });
    safety = setTimeout(function () {
      var t = readTiers(widget);
      conclude(t.length ? 'deal' : 'timeout', t);
    }, KB.SAFETY_TIMEOUT_MS);
    check();
  }

  /* ----- per-card wiring ---------------------------------------------- */

  function activateCard(anchor) {
    if (anchor.__kbActive) return;
    anchor.__kbActive = true;

    var root = anchor.closest(KB.sel.root) || anchor;
    var mount = anchor.querySelector(KB.sel.mount);
    var select = anchor.querySelector(KB.sel.select);
    var productId = mount && mount.getAttribute('data-product-id');
    if (!mount || !select || !productId) return;

    var widget = document.createElement(KB.sel.widget);
    widget.setAttribute('product-id', productId);
    mount.appendChild(widget);

    whenHydrated(widget, function (status, tiers) {
      if (status !== 'deal' || !tiers.length) return; // no deal / timeout: leave dropdown hidden

      select.innerHTML = '';
      var favIndex = -1, packIndex = -1;
      tiers.forEach(function (t, i) {
        var opt = document.createElement('option');
        opt.value = t.id;
        var label = t.price ? (t.title + ' - ' + t.price) : t.title;
        var pct = t.savings ? (t.savings.match(/\d+\s*%/) || [])[0] : null; // "You save 5%" -> "5%"
        if (pct) label += ' (-' + pct.replace(/\s+/g, '') + ')'; // e.g. "10 Pcs - €38,00 (-5%)"
        opt.textContent = label;
        select.appendChild(opt);
        if (t.selected) favIndex = i;
        if (parseInt((t.title || '').replace(/[^0-9]/g, ''), 10) === KB.DEFAULT_PACK) packIndex = i;
      });
      // Default to the 10-pack if the deal has one; otherwise Kaching's
      // "Favorite"; otherwise the first tier. (Kaching's favorite varies per
      // deal — e.g. some are set to 1 Pc — so we don't rely on it alone.)
      var selectedIndex = packIndex >= 0 ? packIndex : (favIndex >= 0 ? favIndex : 0);
      select.selectedIndex = selectedIndex;
      select.disabled = false;

      var wrap = anchor.querySelector(KB.sel.wrap);
      if (wrap) wrap.hidden = false;
      anchor.setAttribute('data-kb-ready', 'true');
      var themeSelect = root.querySelector(KB.sel.themeVariantSelect);
      if (themeSelect) themeSelect.style.display = 'none';

      applyTier(anchor, root, widget, tiers, tiers[selectedIndex].id);
      select.addEventListener('change', function () {
        applyTier(anchor, root, widget, readTiers(widget), select.value);
      });
    });
  }

  // Select a tier inside Kaching and mirror its price onto the visible card.
  // Kaching writes BOTH `quantity` and `properties[__kaching_bundles]` into the
  // form ITSELF, atomically but ASYNCHRONOUSLY. We never touch those fields
  // ourselves (that would desync qty from the discount token); instead we gate
  // the Add button until Kaching's write lands.
  function applyTier(anchor, root, widget, tiers, barId) {
    var tier = tiers.filter(function (t) { return t.id === barId; })[0];
    if (!tier) return;
    tier.label.click(); // <-- the whole trick: Kaching updates the form for us

    var priceEl = root.querySelector(KB.sel.cardPrice); // visual only
    if (priceEl && tier.price) priceEl.textContent = tier.price;

    gateUntilSynced(anchor, barId);
  }

  function gateUntilSynced(anchor, barId) {
    var form = anchor.closest('form');
    if (!form) return;
    var btns = form.querySelectorAll(KB.sel.addButton);
    var propInput = form.querySelector('input[name="properties[__kaching_bundles]"]');
    var synced = function () {
      return propInput && propInput.value.indexOf('"bid":"' + barId + '"') !== -1;
    };
    if (synced()) return;
    Array.prototype.forEach.call(btns, function (b) {
      if (b.disabled) return; // leave genuinely-disabled buttons (e.g. sold out) alone
      b.setAttribute('data-kb-gated', '1');
      b.disabled = true;
    });
    var tries = 0;
    var iv = setInterval(function () {
      if (synced() || ++tries > 30) { // ~1.5s ceiling
        clearInterval(iv);
        Array.prototype.forEach.call(btns, function (b) {
          if (b.getAttribute('data-kb-gated')) { b.removeAttribute('data-kb-gated'); b.disabled = false; }
        });
      }
    }, 50);
  }

  /* ----- activation ---------------------------------------------------- */

  function activateAll() {
    Array.prototype.forEach.call(document.querySelectorAll(KB.sel.card), activateCard);
  }

  function boot() {
    activateAll();

    // Cards added later (AJAX pagination / filtering / infinite scroll) — a
    // MutationObserver is more reliable than theme-specific events.
    if (window.__kbMO) return;
    window.__kbMO = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(KB.sel.card)) { activateCard(node); }
          else if (node.querySelectorAll) {
            Array.prototype.forEach.call(node.querySelectorAll(KB.sel.card), activateCard);
          }
        }
      }
    });
    window.__kbMO.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();